import React from 'react';
import * as Sentry from '@sentry/react';
import {useParams} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {useSummary} from '../../../../context/summary';
import {
  useDocumentSelfieDetection,
  useModalControls,
  useScreenResize,
} from '../../../../hooks';
import {ButtonProps} from 'components/common/Button/Button';
import {IV_PHOTO_SOURCE, IV_DOCUMENT_IMAGE_TYPES} from '@guestapp/sdk';
import {device} from 'styled/device';
import passportGif from 'assets/img/ocr-passport-info.gif?no-inline';
import frontSidePlaceholder from '../../../../assets/img/frontside-placeholder.svg?no-inline';
import PhotoBox from '../../../../components/PhotoBox';
import MRZPageLayout from '../../MRZPageLayout';
import ModalSheet from 'components/common/ModalSheet';
import StandardModalSheet from 'components/common/StandardModalSheet';
import {HelperText, QuestionButton, ModalContent} from './styled';

function MRZScanView() {
  const {t} = useTranslation();
  const theme = useTheme();
  const {uploadType} = useParams();
  const {reservationId, countryCode, data: summary} = useSummary();
  const {isMatch: isMobileMode} = useScreenResize(device.mobileXL);
  const {ProcessModal, ErrorModal, RetryModal, sendAndReadImageForOCR} =
    useDocumentSelfieDetection({
      docType: IV_DOCUMENT_IMAGE_TYPES.back,
      onlyScanning: true,
    });

  const isUpload = uploadType === 'upload';
  const mobilePageTitle = isUpload && isMobileMode ? 'guest_data' : 'scan_your_document';
  const {
    isOpen: isOpenNeedHelpModal,
    openModal: openNeedHelpModal,
    closeModal: closeNeedHelpModal,
  } = useModalControls();

  const modalSheetButtons: ButtonProps[] = React.useMemo(() => {
    const primaryButton = {
      label: t('understood'),
      primary: true,
      onClick: closeNeedHelpModal,
    };
    return isMobileMode ? [] : [primaryButton];
  }, [t, closeNeedHelpModal, isMobileMode]);

  const handleChangePhoto = React.useCallback(
    ({image, source}: {image: string; source: IV_PHOTO_SOURCE}) => {
      if (countryCode) {
        sendAndReadImageForOCR({image, source, countryCode, reservationId});
      } else {
        console.error('countryCode is missing', summary);
        Sentry.captureMessage('countryCode is missing', {extra: {summary}});
      }
    },
    [countryCode, reservationId, sendAndReadImageForOCR, summary],
  );

  const modalContent = (
    <ModalContent>
      <div>
        <Trans i18nKey="tool_tip_mrz_text_one">
          Make sure the photo of your document has enough quality to detect the{' '}
          <b>MRZ code</b> of the document.
        </Trans>
      </div>
      <div>{t('tool_tip_mrz_text_two')}</div>
    </ModalContent>
  );

  return (
    <MRZPageLayout title={t(mobilePageTitle)}>
      <HelperText className="mrz-scan-view__helper">
        {t('upload_mrz_helper_text')}
      </HelperText>
      <QuestionButton
        iconProps={{
          position: 'left',
          color: theme.colors.buttons.textButton,
          size: 16,
        }}
        iconName="questionCircleBlue"
        // customIcon={<img width={16} height={16} src={questionIcon} alt="Need help?" />}
        label={t('need_help_question')}
        onClick={openNeedHelpModal}
        className="mrz-scan-view__question-btn"
        link
      />
      <PhotoBox
        mobileHelperTitle={t('scan_your_document')}
        mobileHelperText={t('code_mrz_helper_text')}
        onUploadPhoto={handleChangePhoto}
        preloadedScreenshot={''}
        isUpload={isUpload}
        placeholderImage={frontSidePlaceholder}
      />
      <ProcessModal />
      <ErrorModal />
      <RetryModal />

      <ModalSheet
        isOpenModalSheet={isOpenNeedHelpModal}
        handleCloseModal={closeNeedHelpModal}
      >
        <StandardModalSheet
          buttons={modalSheetButtons}
          text={modalContent}
          img={passportGif}
        />
      </ModalSheet>
    </MRZPageLayout>
  );
}

export {MRZScanView};
